<template>
  <div class="row">
    <div v-show="!isFullview" class="col-3">
      <!-- Title and Search -->
      <div class="row">
        <div class="col">
          <h4>Locations</h4>
        </div>
      </div>
      <div class="row mb-1">
        <div class="col">
          <div class="input-group">
            <button type="button" class="btn btn-primary btn-sm" @click="refreshLocations">
              <i class="fas fa-rotate"></i>
            </button>
            <input v-model="searchText" type="text" class="form-control" placeholder="search for a location"
              @keyup.enter="searchLocations" />

            <button type="button" class="btn btn-warning btn-sm custom-btn-focus" title="clear search"
              @click="clearSearch">
              <i class="fas fa-xmark"></i>
            </button>
            <button type="button" class="btn btn-primary btn-sm custom-btn-focus custom-divider" title="search tree"
              @click="searchLocations">
              <i class="fas fa-search"></i>
            </button>
            <button type="button" class="btn btn-primary btn-sm custom-btn-focus custom-divider" title="advanced search"
              @click="advancedSearch">
              <i class="fas fa-search"></i>
              <i class="fas fa-bolt custom-bolt"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="col custom-panel">
      <div class="row">
        <div class="col custom-blank">
          <div class="custom-hide" @click="isFullview = !isFullview">
            <FontAwesomeIcon v-if="!isFullview" icon="fa-solid fa-chevron-left" />
            <FontAwesomeIcon v-else icon="fa-solid fa-chevron-right" />
          </div>
        </div>
      </div>
      <div class="row">
        <div v-if="isImportExport && !isInsurance" class="col text-right">
          <button v-if="isAdmin && isSelected" class="btn btn-primary btn-sm" @click="importLocations">Import
            Locations</button>
          <button v-if="isAdmin && isSelected" class="btn btn-secondary btn-sm ms-2" @click="exportLocations">Export
            Locations</button>
          <button v-if="isAdmin && isSelected" class="btn btn-primary btn-sm ms-2" @click="importUsers">Import
            Users</button>
          <button v-if="isAdmin && isSelected" class="btn btn-secondary btn-sm ms-2" @click="exportUsers">Export
            Users</button>
        </div>
        <div v-else class="col"></div>
      </div>
      <!-- Action Bar -->
      <div class="row mt-2">
        <div class="col">
          <div class="ms-4">
            <h4 class="d-inline custom-title">
              {{ title }}
            </h4>
            <span class="d-inline custom-subtitle">{{ subtitle }}</span>
            <span v-if="firmClass !== null" class="d-inline custom-firmclass ms-2">({{ firmClass }})</span>
          </div>
        </div>
        <div v-if="isSelected" class="col-4">
          <button class="btn btn-primary ms-3" :class="{ 'btn-danger': isSave }" @click="save">Save</button>
          <button v-if="!isShop && !isInsurance" class="btn btn-primary btn-sm ms-4" @click="addNewLocation">Add
            Location</button>
          <span v-if="isTopmost" class="custom-add-location">only 'add' at this level if this will be a 'White Label'
            partner</span>
        </div>
      </div>
    </div>
  </div>
  <div class="row custom-location-wrapper">
    <div v-show="!isFullview && !isAdvancedSearch" class="col-3">
      <!-- Location Spinner (while loading up the Location Profile) -->
      <div v-if="loading.locationTree" class="loadingWrapper">
        <div class="loadingSpinner">
          <FontAwesomeIcon icon="fa-solid fa-spinner me-2" spin />
          <span>loading...</span>
        </div>
      </div>
      <!-- Location Tree (on the left) -->
      <div v-else class="custom-locations-wrapper">
        <div class="row">
          <div class="col">
            <tree-node id="LocationTree" :tree="locationTree" :depth="0" :show-type="false" :is-open="true"
              :select-function="selectLocation" :class-function="getClass" :is-show-icon="true" />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <input v-model="showInactive" type="checkbox" class="custom-checkbox ms-4 me-2 mt-3"
              @change="refreshLocations" />
            <span>show inactive</span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!isFullview && isAdvancedSearch" class="col-3">
      <!-- Location Spinner (while loading up the Location Profile) -->
      <div v-if="loading.advancedLocations" class="loadingWrapper">
        <div class="loadingSpinner">
          <FontAwesomeIcon icon="fa-solid fa-spinner me-2" spin />
          <span>loading...</span>
        </div>
      </div>
      <div v-else class="custom-locations-wrapper">
        <ul class="custom-advanced-location-list">
          <li v-for="location in advancedLocations" :key="location.firm">
            <div class="tree-node-wrapper"
              :class="{ 'custom-location-selected': selectedFirm ? selectedFirm.id === location.id : '' }"
              @click="selectLocation(location)">
              <div class="label-wrapper">
                <span>{{ location.name }}</span>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <!-- Location Details (on the right) -->
    <div class="col custom-panel" :class="{ 'col-12': isFullview }">
      <location-host v-if="isSelected" ref="host" :firm="selectedFirm" @save="isSave = true" @saved="savedLocation"
        @moved="movedLocation" />
    </div>
  </div>
  <!-- Dialogs -->
  <dialog-add-location v-model="actionLocation" @closing="closeModal" @firmSaved="firmSaved" @saved="savedLocation" />
  <dialog-import v-model="actionImport" @closing="closeModal" @saved="saved" />
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { authStore } from '@/stores/auth'
import { lookupStore } from '@/stores/lookup'
import { locationStore } from '@/stores/location'
import { dataStore } from '@/stores/data'
import { Parser } from '@json2csv/plainjs'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import TreeNode from '@/components/utilities/elements/TreeNode.vue'
import LocationHost from '@/components/locations/forms/LocationHost.vue'
import DialogAddLocation from '@/components/locations/forms/DialogAddLocation.vue'
import DialogImport from '@/components/configuration/data/DialogImport.vue'
export default {
  name: 'LocationTree',
  components: {
    FontAwesomeIcon,
    TreeNode,
    LocationHost,
    DialogAddLocation,
    DialogImport
  },
  data() {
    return {
      actionLocation: null,
      selectedFirm: null,
      isSelected: false,
      searchText: null,
      isSave: false,
      searchedTree: [],
      actionImport: null,
      isScroll: false,
      showInactive: false,
      isFullview: false,
      isAdvancedSearch: false
    }
  },
  async mounted() {
    this.isSelected = false
    this.selectedFirm = null
    this.showInactive = false
    this.isAdvancedSearch = false
    await this.getLookups({
      keys: ['Language', 'TimeZone', 'FirmType', 'FirmClass'],
      firm: 1,
      refresh: false
    })
    this.getLocationTree({
      firm: this.profile.firm,
      showInactive: this.showInactive
    })
  },
  computed: {
    ...mapState(authStore, ['profile']),
    ...mapState(locationStore, ['loading', 'location', 'addLocation', 'locationTree', 'advancedLocations']),
    ...mapState(dataStore, ['locations', 'users']),
    title() {
      let _title = ''
      if (this.selectedFirm) {
        _title = this.selectedFirm.name
      }
      return _title
    },
    subtitle() {
      let _subtitle = ''
      if (this.selectedFirm) {
        _subtitle = this.selectedFirm.type
      }
      return _subtitle
    },
    firmClass() {
      let _firmClass = null
      if (this.selectedFirm) {
        _firmClass = this.selectedFirm.firmClass
      }
      return _firmClass
    },
    isAdmin() {
      let _is = false
      if ([1, 2, 3, 4, 7].includes(this.profile.roles[0])) {
        _is = true
      }
      return _is
    },
    isInsurance() {
      let _is = false
      if (this.location) {
        if (this.location.firmClass === 2) {
          _is = true
        }
      }
      return _is
    },
    isProvider() {
      let _is = false
      if (this.location) {
        if (this.location.firmClass === 5) {
          _is = true
        }
      }
      return _is
    },
    isCalCopilot() {
      let _data = false
      if (this.location) {
        _data = this.location.isCalCopilot
      }
      return _data
    },
    isShop() {
      let _is = false
      if (this.selectedFirm) {
        if (this.selectedFirm.type === 'Shop') {
          _is = true
        }
      }
      return _is
    },
    isImportExport() {
      let _is = false
      if (this.selectedFirm) {
        if (this.selectedFirm.type === 'Corporate') {
          _is = true
        }
      }
      return _is
    },
    isTopmost() {
      let _is = false
      if (this.selectedFirm) {
        if (this.selectedFirm.firm === 1) {
          _is = true
        }
      }
      return _is
    }
  },
  methods: {
    ...mapActions(lookupStore, ['getLookups']),
    ...mapActions(locationStore, [
      'getLocationTree',
      'getLocation',
      'resetAddLocation',
      'setAddLocation',
      'setLocation',
      'deleteLocation',
      'searchAdvanced'
    ]),
    ...mapActions(dataStore, ['getLocationsForExport', 'getUsersForExport']),
    closeModal() {
      this.actionLocation = null
      this.actionImport = null
    },
    async refreshLocations() {
      this.isSelected = false
      this.isAdvancedSearch = false
      this.searchText = null
      await this.getLocationTree({
        firm: this.profile.firm,
        showInactive: this.showInactive
      })
    },
    firmSaved(data) {
      console.log('Firm Saved', data)
    },
    importLocations() {
      this.actionImport = {
        title: 'Import Locations',
        parentBranch: this.selectedFirm.firm
      }
    },
    importUsers() {
      this.actionImport = {
        title: 'Import Users'
      }
    },
    async exportLocations() {
      try {
        await this.getLocationsForExport(this.selectedFirm.firm)
        if (this.locations) {
          const parser = new Parser()
          const csv = parser.parse(this.locations)
          console.log(csv)
          // download the file as CSV
          var downloadLink = document.createElement('a')
          var blob = new Blob(['\ufeff', csv])
          var url = URL.createObjectURL(blob)
          downloadLink.href = url
          downloadLink.download = `Locations for ${this.selectedFirm.name}.csv`
          document.body.appendChild(downloadLink)
          downloadLink.click()
          document.body.removeChild(downloadLink)
        }
      } catch (err) {
        console.error(err)
      }
    },
    async exportUsers() {
      try {
        await this.getUsersForExport(this.selectedFirm.firm)
        const parser = new Parser()
        const csv = parser.parse(this.users)
        console.log(csv)
        // download the file as CSV
        var downloadLink = document.createElement('a')
        var blob = new Blob(['\ufeff', csv])
        var url = URL.createObjectURL(blob)
        downloadLink.href = url
        downloadLink.download = `Users for ${this.selectedFirm.name}.csv`
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      } catch (err) {
        console.error(err)
      }
    },
    saved() {
      this.actionImport = null
      this.refreshLocations()
    },
    async addNewLocation() {
      this.resetAddLocation()
      this.setAddLocation({
        name: 'parent',
        value: this.selectedFirm.firm
      })
      let firmType = 2
      let firmTypeName = ''
      switch (this.selectedFirm.type) {
        case 'Corporate':
          firmType = 3
          firmTypeName = 'Division'
          break
        case 'Division':
          firmType = 4
          firmTypeName = 'Distributor'
          break
        case 'Distributor':
          firmType = 5
          firmTypeName = 'Partner'
          break
        case 'Partner':
          firmType = 6
          firmTypeName = 'Center'
          break
        case 'Center':
          firmType = 7
          firmTypeName = 'Shop'
          break
        default:
          break
      }
      this.setAddLocation({
        name: 'firmType',
        value: firmType
      })
      console.log('Add Location - Current Location', this.addLocation)
      this.actionLocation = {
        isAdd: true,
        parentFirm: this.selectedFirm.name,
        firmType: firmTypeName
      }
    },
    async movedLocation() {
      await this.refreshLocations()
      this.searchLocations()
    },
    async savedLocation(isChanged) {
      this.isSave = false
      if (isChanged) {
        this.searchText = null
        this.actionLocation = null
        await this.getLocationTree({
          firm: this.profile.firm,
          showInactive: this.showInactive
        })
        this.searchLocations()
      }
    },
    save() {
      this.$refs.host.save()
    },
    async selectLocation(tree) {
      // select our location and get
      // the location information
      await this.getLocation(tree.id)
      console.log('Selecting Firm', tree)
      this.selectedFirm = {
        parent: tree.parent,
        id: tree.id,
        firm: tree.id,
        name: tree.name,
        type: tree.type,
        contact: tree.contact,
        firmClass: tree.firmClass
      }
      this.isSelected = true
      this.setSelection(tree.id)
      this.isSave = false
      if (this.$refs.host) {
        await this.$refs.host.init(tree.id)
      }
    },
    setSelection(id) {
      if (!this.isAdvancedSearch) {
        // highlight the node in the tree
        var x = document.getElementsByClassName('tree-node-selection')
        if (x !== undefined && x !== null) {
          for (var i = 0; i < x.length; i++) {
            x[i].classList.remove('tree-node-selection')
          }
          x = document.getElementById(id)
          x.classList.add('tree-node-selection')
          if (this.isScroll) {
            x.scrollIntoView()
          }
        }
      }
    },
    resetFound() {
      var x = document.getElementsByClassName('tree-node-found')
      if (x !== undefined && x !== null) {
        for (var i = 0; i < x.length; i++) {
          x[i].classList.remove('tree-node-found')
        }
      }
    },
    setFound(id) {
      var x = document.getElementById(id)
      if (x) {
        x.classList.add('tree-node-found')
      }
    },
    closeTreeNodes(node, rootPath) {
      // loop through the tree and set
      // the isOpen flag to false, closing
      // all nodes
      rootPath.push(node)
      node.isOpen = false
      if (node.members) {
        if (node.members.length > 0) {
          node.members.forEach((element) => {
            this.closeTreeNodes(element, rootPath)
          })
        }
      }
      rootPath.pop()
    },
    openTreeNode(id, node, rootPath) {
      // loop through the tree and set
      // the isOpen flag to open up the
      // appropriate nodes
      rootPath.push(node)
      if (node.id === id) {
        node.isOpen = true
      }
      if (node.members) {
        if (node.members.length > 0) {
          node.members.forEach((element) => {
            this.openTreeNode(id, element, rootPath)
          })
        }
      }
      rootPath.pop()
    },
    traverseTree(search, node, rootPath) {
      // this is an iterative tree search to find our
      // location based upon name
      rootPath.push(node)
      if (node.name.includes(search)) {
        rootPath.forEach((element) => {
          this.searchedTree.push({
            id: element.id,
            name: element.name,
            type: element.type
          })
        })
      }
      if (node.members) {
        if (node.members.length > 0) {
          node.members.forEach((element) => {
            this.traverseTree(search, element, rootPath)
          })
        }
      }
      rootPath.pop()
    },
    traverseTreeById(id, node, rootPath) {
      // this is an iterative tree search to find our
      // location based upon name
      rootPath.push(node)
      if (parseInt(node.id) === parseInt(id)) {
        rootPath.forEach((element) => {
          this.searchedTree.push({
            id: element.id,
            name: element.name,
            type: element.type
          })
        })
      }
      if (node.members) {
        if (node.members.length > 0) {
          node.members.forEach((element) => {
            this.traverseTreeById(id, element, rootPath)
          })
        }
      }
      rootPath.pop()
    },
    clearSearch() {
      this.resetFound()
      this.selectedFirm = null
      this.refreshLocations()
    },
    async searchLocations() {
      let path = []
      if (this.searchText === null) {
        this.searchText = ''
      }
      this.isAdvancedSearch = false
      if (this.searchText.length !== 0) {
        // close all nodes first
        this.closeTreeNodes(this.locationTree, path)
        this.resetFound()
        path = []
        this.searchedTree = []
        // traverse the tree looking for location
        if (this.searchText) {
          this.traverseTree(this.searchText, this.locationTree, path)
        } else {
          this.traverseTreeById(this.selectedFirm.id, this.locationTree, path)
        }
        // if found, open up the tree accordingly
        if (this.searchedTree.length > 0) {
          path = []
          for (let idx = 0; idx < this.searchedTree.length; idx++) {
            const element = this.searchedTree[idx]
            this.openTreeNode(element.id, this.locationTree, path)
            this.$forceUpdate()
            // and select it...
            if (idx === this.searchedTree.length - 1) {
              this.isScroll = true
              await this.selectLocation(element)
              this.isScroll = false
            }
          }
          if (this.searchText) {
            for (let idx = 0; idx < this.searchedTree.length; idx++) {
              const element = this.searchedTree[idx]
              // indicate found
              if (element.name.includes(this.searchText)) {
                this.setFound(element.id)
              }
            }
          }
        }
      }
    },
    async advancedSearch() {
      this.isAdvancedSearch = false
      if (this.searchText === null) {
        this.searchText = ''
      }
      if (this.searchText.length !== 0) {
        let options = {
          firm: this.profile.firm,
          searchTerm: this.searchText,
          filter: this.showInactive ? 'showInactive' : null,
          type: 'advancedSearch'
        }
        await this.searchAdvanced(options)
        if (this.advancedLocations) {
          this.isAdvancedSearch = true
        }
      }
    },
    getClass(type) {
      let result = null
      switch (type) {
        case 'Admin':
          result = 'custom-dark-green'
          break
        case 'Corporate':
          result = 'custom-orange'
          break
        case 'Division':
          result = 'custom-blue-italic'
          break
        case 'Distributor':
          result = 'custom-green'
          break
        case 'Partner':
          result = 'custom-black-italic'
          break
        case 'Center':
          result = 'custom-green-italic'
          break
        default:
          break
      }
      return result
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-blank {
  height: 30px;
  position: absolute;
}

.custom-panel {
  position: relative;
  border-left: 1px solid #b8b8b8;
}

.custom-location-wrapper {
  min-height: 250px;
}

.custom-hide {
  position: absolute;
  padding-left: 5px;
  padding-top: 5px;
  background-color: #007bff;
  color: white;
  left: 0;
  border: 1px solid #007bff;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  height: 36px;
  width: 25px;
  z-index: 10;

  :hover {
    cursor: pointer;
  }
}

.custom-locations-wrapper {
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;
}

.loadingWrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.loadingSpinner {
  margin-top: 40px;
  text-align: center;
  font-weight: 700;
  width: 100%;
  background: rgba(255, 255, 255, 0.8);
  padding: 10px;

  span {
    margin-left: 5px;
  }
}

.tree-node-selection {
  background-color: rgb(255, 255, 187);
}

.tree-node-wrapper {
  border-bottom: 1px solid #b8b8b8;
  padding-top: 10px;
  padding-bottom: 10px;
  cursor: pointer;
}

.tree-node {
  .label-wrapper {
    padding-bottom: 10px;
    margin-bottom: 10px;
    border-bottom: 1px solid #ccc;

    .has-children {
      cursor: pointer;
    }
  }
}

.TeamTree {
  max-height: 500px;
  overflow: auto;
  overflow-x: hidden;
  margin-top: 15px;
}

.tree-link {
  width: 100%;

  a {
    display: inline-block;
    width: 100%;
  }
}

.tree-link:hover {
  color: white;
  background: #2f9af7;
}

.tree-node-remove {
  text-align: center;
  color: #b30505;
}

.titleRight {
  position: absolute;
  right: 10px;
  top: 10px;
  font-weight: 500;
}

.custom-actions {
  float: right;
}

.custom-subtitle {
  font-style: italic;
  margin-left: 10px;
}

.custom-firmclass {
  font-style: italic;
  font-size: 0.8em;
  margin-left: 10px;
}

.custom-delete {
  float: right;
}

.custom-add-location {
  position: absolute;
  bottom: -15px;
  left: 110px;
  color: red;
  font-size: 0.8em;
  font-weight: 600;
}

.custom-advanced-location-list {
  list-style-type: none;
  padding-left: 20px;

  :hover {
    cursor: pointer;
    background-color: rgb(219, 219, 219);
  }
}

.custom-btn-focus:focus {
  border-color: inherit;
  -webkit-box-shadow: none;
  box-shadow: none;
  outline: none;
  border: 1px solid #ced4da;
}

.custom-divider {
  border-left: #ced4da 1px solid;
}

.custom-bolt {
  position: absolute;
  top: 3px;
  right: 2px;
  font-size: 0.8em;
}

.custom-location-selected {
  background-color: rgb(255, 255, 187);
}
</style>
